import {
  Component,
  OnInit,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import {
  DvDynamicFormElement,
  DvDynamicFormUtils,
  DvFormUtils,
  DvTabBase,
  FormOf,
} from '@devinco/ui/legacy';
import { ToolbarActionsService } from '@features/vt-grid/services/toolbar-actions.service';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { VehicleType, VehicleTypeID } from '@shared/constants/vehicle-type';
import { TranslateService } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
  combineLatest,
  concatMap,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { VehicleDto } from '@pages/vehicles-page/components/vehicles-grid/interfaces';
import { VehicleInformationTabForm } from './interfaces';
import { VehicleInfoTabFormService } from './vehicle-information-tab.form.service';
import { VehicleInformationTabService } from './vehicle-information-tab.service';
import { DialogService } from '@core/services/dialog.service';
import { DvDialogComponent, DvDialogCloseEvent } from '@devinco/ui/dds';
import { VtSplitterService } from '@shared/services/grid/vt-splitter.service';
import { EventBusService } from '@shared/services/event-bus/event-bus.service';
import { EventKey } from '@shared/constants/event-bus/event-key';
import { QuickFilterModel } from '@shared/constants/quick-filters/quick-filter-models';
import { UserMetadataService } from '@core/services/user-metadata.service';
import DynamicFormUtils from '@shared/utils/dynamic-form.utils';

@Component({
  templateUrl: './vehicle-information-tab.component.html',
  providers: [VehicleInformationTabService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VehicleInformationTabComponent
  extends DvTabBase
  implements PendingChanges, OnInit
{
  private agGridEntityService = inject(SplitterWithGridHelperService);
  private api = inject(RestApiService);
  private tab = inject(VehicleInformationTabService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private translate = inject(TranslateService);
  private dialog = inject(DialogService);
  private toolbarActionsService = inject(ToolbarActionsService);
  private splitterService = inject(VtSplitterService);
  private oidc = inject(OidcSecurityService);
  private formService = inject(VehicleInfoTabFormService);
  private eventBusService = inject(EventBusService);
  protected readonly metaDataService = inject(UserMetadataService);

  formGroup: FormGroup<FormOf<VehicleInformationTabForm>>;
  elements: DvDynamicFormElement[];
  vehicleElements: DvDynamicFormElement[];
  viaTracksElements: DvDynamicFormElement[];
  skiDomainElements: DvDynamicFormElement[];
  createdTime: Date;
  isVehicleDeleted: boolean;

  saveInProgress = signal<boolean>(false);

  private initialVehicleType: number | undefined;
  private initialRegistrationNumber: string | undefined;
  private hasVehicleTypeChanged: boolean;
  private hasVehicleEditPermission: boolean;
  private hasEditMyPrivatePermission: boolean;

  ngOnInit(): void {
    this.vehicleElements = this.tab.elementsVehicle;
    this.viaTracksElements = this.tab.elementsVT;
    this.skiDomainElements = this.tab.getSkiDomainElements();
    this.elements = [
      ...this.vehicleElements,
      ...this.viaTracksElements,
      ...this.skiDomainElements,
    ];
    this.formGroup = DvFormUtils.createFormGroup(this.elements, true);
    this.subscribeToVehicleTypeChange();
    this.subscribeToFuelTypeChanges();
    this.subscribeToVehicleDeletedTrigger();
    this.subscribeIsRouteNetworkMaintainer();
  }

  hasChanges = (): boolean => this.formGroup.dirty && this.formGroup.valid;
  pendingChangesSave = (): Observable<boolean> =>
    this.performSave().pipe(tap(() => this.saveInProgress.set(false)));

  onSave(): void {
    this.performSave()
      .pipe(
        take(1),
        tap(() => this.saveInProgress.set(false))
      )
      .subscribe();
  }

  onReactivateClick(): void {
    this.api
      .upsert(Entity.Vehicle, this.entityId, { ShowRecord: true })
      .pipe(take(1))
      .subscribe(_ => {
        this.formGroup.enable();
        this.isVehicleDeleted = false;
        this.agGridEntityService.refreshRowData<VehicleDto>(this.entityId);
        this.setMainGridsFilterModel();
        this.onEntityUpdate();
        this.agGridEntityService
          .getGridApi()
          .getRowNode(this.entityId)
          ?.setSelected(true, true);
      });
  }

  onBaseActions(): void {
    this.initialVehicleType = undefined;
    this.hasVehicleTypeChanged = false;
  }

  onEntityCreate(): void {
    this.formGroup.enable();
    this.formGroup.reset();
    this.isVehicleDeleted = false;
    this.initialRegistrationNumber = undefined;
    DvDynamicFormUtils.setFirstData(this.elements, this.formGroup, {
      VehicleTypeID: 1,
      VehicleTypeDescription: this.translate.instant(VehicleType.Corporate),
    });
    this.setDefaultValues();
  }

  onEntityUpdate(): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!this.formGroup) {
      return;
    }
    this.formGroup.controls.Mileage.disable();
    const keys = DvDynamicFormUtils.getKeysForRequest(this.elements);
    combineLatest([
      this.api.getInfo<VehicleInformationTabForm>(
        Entity.Vehicle,
        this.entityId,
        keys
      ),
      this.oidc.getUserData(),
    ])
      .pipe(take(1))
      .subscribe(([res, userData]) => {
        this.initialRegistrationNumber = res.RegistrationNumber;
        this.formGroup.enable();
        this.formGroup.controls.ExpenseSetupID.disable();
        this.handlePermissionsOnEntityUpdate(userData.email, res);

        const { controls } = this.formGroup;
        this.createdTime = res.CreatedTime;
        const data = this.formService.prepareDataForForm(res);
        // set UserID for default driver dropdown valueKey
        data.UserID = data.DefaultUserID;

        if (data.VehicleTypeDescription) {
          data.VehicleTypeDescription = this.translate.instant(
            `VehicleType.${data.VehicleTypeDescription}`
          );
        }
        DvDynamicFormUtils.setFirstData(this.elements, this.formGroup, data);

        // manually setting numeric data since it does not get set via setFirstData (?)
        this.formGroup.controls.FuelConsumption.setValue(data.FuelConsumption);

        this.isVehicleDeleted = !res.ShowRecord;

        this.toggleVehicleTypeDropdownOnEntityUpdate(res.VehicleTypeID);

        if (this.isVehicleDeleted) {
          this.formGroup.disable();
          return;
        }

        this.initialVehicleType = data.VehicleTypeID;

        if (!this.hasVehicleEditPermission && !this.hasEditMyPrivatePermission)
          return;

        this.formService.toggleControlsForExistingVehicleType(
          data.VehicleTypeID,
          this.formGroup.controls,
          this.hasVehicleEditPermission
        );
        if (!data.HasFind) {
          controls.IsAssetLocation?.disable();
        }
      });
  }

  private toggleVehicleTypeDropdownOnEntityUpdate(
    vehicleTypeId: VehicleTypeID | undefined
  ): void {
    const vehicleTypeIdControl = this.formGroup.controls.VehicleTypeID;
    if (vehicleTypeId === VehicleTypeID.Personal) {
      vehicleTypeIdControl?.disable();
    } else {
      vehicleTypeIdControl?.enable();
    }
  }

  private handlePermissionsOnEntityUpdate(
    userEmail: string,
    res: VehicleInformationTabForm
  ): void {
    const isUsersPrivateVehicle =
      userEmail.toLowerCase() === (res.Username || '').toLowerCase() &&
      res.VehicleTypeDescription === VehicleType.Personal;
    this.handleAccessRestrictions(isUsersPrivateVehicle);
  }

  private setDefaultValues(): void {
    const request: GetRowsRequest = {
      entity: Entity.ExpenseSetupVehicle,
      fields: ['ExpenseSetupID', 'ExpenseSetupDescription'],
    };
    this.api.getData(request).subscribe(res => {
      if (res.data.length) {
        const { ExpenseSetupID } = this.formGroup.controls;
        ExpenseSetupID.setValue(res.data[0].ExpenseSetupID);
        ExpenseSetupID.markAsDirty();
        ExpenseSetupID.disable();
      }
    });
    this.formService.toggleEditableFieldsForNewVehicle(this.formGroup.controls);
  }

  private subscribeToVehicleTypeChange(): void {
    this.formGroup.controls.VehicleTypeID?.valueChanges
      .pipe(
        filter(() => !!this.formGroup.controls.VehicleTypeID?.dirty),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(vehicleTypeId => {
        this.hasVehicleTypeChanged = vehicleTypeId !== this.initialVehicleType;
        if (this.entityId === NEW_ENTITY_ROUTE_PARAM) {
          this.formService.toggleEditableFieldsForNewVehicle(
            this.formGroup.controls
          );
        } else {
          this.formService.toggleControlsForExistingVehicleType(
            vehicleTypeId,
            this.formGroup.controls,
            this.hasVehicleEditPermission
          );
        }
      });
  }

  private subscribeToFuelTypeChanges(): void {
    this.formGroup.controls.FuelTypeID.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(fuelTypeId => {
        this.formService.triggerFuelConsumptionLabel(this.elements, fuelTypeId);
        this.elements = [...this.elements];
      });
  }

  private performSave(): Observable<boolean> {
    this.saveInProgress.set(true);
    if (this.entityId === NEW_ENTITY_ROUTE_PARAM) {
      return this.addNewVehicle();
    } else {
      const dirtyValues = DvFormUtils.getDirtyValues(
        this.formGroup
      ) as VehicleInformationTabForm;
      if (
        'RegistrationNumber' in dirtyValues &&
        dirtyValues.RegistrationNumber !== this.initialRegistrationNumber
      ) {
        return this.openRegistrationNumberChangedDialog().closed.pipe(
          switchMap(({ source }) => {
            if (source !== 'confirm') return of(true);

            this.initialRegistrationNumber = dirtyValues.RegistrationNumber;

            return this.updateVehicle(dirtyValues);
          })
        );
      }

      if (dirtyValues.VehicleTypeID === VehicleTypeID.Personal) {
        this.formGroup.controls.VehicleTypeID?.disable();
      }
      return this.updateVehicle(dirtyValues);
    }
  }

  private copyVehicle(entityId: number): Observable<boolean> {
    return this.openVehicleCopyNeededDialog().closed.pipe(
      switchMap(({ source }) => {
        if (source === 'confirm') {
          this.formGroup.markAsPristine();
          return this.api.delete(Entity.Vehicle, entityId).pipe(
            switchMap(() => {
              this.removeSelectedRow();
              const payload =
                this.formGroup.getRawValue() as Partial<VehicleInformationTabForm>;
              // properties that shouldn't be saved
              delete payload.VehicleID;
              delete payload.HasTrip;
              delete payload.HasFind;
              delete payload.CreatedTime;
              return this.api.upsert(Entity.Vehicle, 0, payload);
            }),
            tap(id => {
              this.formGroup.reset();
              if (id) {
                this.agGridEntityService.addNewlyCreatedItemToGrid(
                  Entity.Vehicle,
                  id
                );
              }
            }),
            map(() => true)
          );
        } else {
          return of(true);
        }
      })
    );
  }

  private openOldTripsExistDialog(): Observable<
    DvDialogCloseEvent<unknown, unknown>
  > {
    return this.dialog.open({
      size: 'md',
      type: 'warning',
      title: this.translate.instant('General.Dialog.Confirmation'),
      contentText: this.translate.instant('Vehicle.OldTripsExist'),
      confirmText: 'General.Yes',
      cancelText: 'General.No',
    }).closed;
  }

  private openVehicleCopyNeededDialog(): DvDialogComponent<undefined, unknown> {
    return this.dialog.open({
      size: 'md',
      type: 'warning',
      title: 'Vehicle.CreateVehicleCopyTitle',
      contentText: this.translate.instant('Vehicle.CreateVehicleCopyText', {
        vehicleDescription: this.formGroup.controls.VehicleDescription?.value,
      }),
      confirmText: 'General.Confirm',
      cancelText: 'General.Cancel',
    });
  }

  private upsertExistingVehicle(
    entityId: string,
    data: Partial<VehicleInformationTabForm>,
    customCheckDone: boolean = false
  ): Observable<boolean> {
    return this.api.upsert(Entity.Vehicle, entityId, data).pipe(
      switchMap(response => {
        if (
          response &&
          typeof response === 'object' &&
          'CustomCheck' in response &&
          !customCheckDone
        ) {
          return this.openOldTripsExistDialog();
        } else {
          return of(null);
        }
      }),
      concatMap(event => {
        if (event?.source === 'cancel') {
          return this.api.upsert(Entity.Vehicle, entityId, {
            ...data,
            // skip specific vehicle check for tracker trips (required by backend)
            IsCustomCheckConfirmed: false,
          });
        } else if (event?.source === 'confirm') {
          return this.api.upsert(Entity.Vehicle, entityId, {
            ...data,
            // execute custom vehicle tracker trips assignment (required by backend)
            IsCustomCheckConfirmed: true,
          });
        } else {
          return of(true);
        }
      }),
      tap(_ => {
        this.formGroup.markAsPristine();
      }),
      switchMap(_ => {
        return this.agGridEntityService.refreshRowData<VehicleDto>(entityId);
      }),
      filter(resp => !!resp),
      take(1),
      tap(resp => {
        this.formGroup.controls.AllowShowInMap?.setValue(!!resp.AllowShowInMap);
      }),
      map(_ => true)
    );
  }

  private handleAccessRestrictions(isUsersPrivateVehicle = false): void {
    combineLatest([
      this.permissionsService.hasPermissions([RolesAndPermissions.VehicleEdit]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.AssetLocationSettingsEdit,
      ]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.VehicleEditMyPrivate,
      ]),
    ])
      .pipe(take(1))
      .subscribe(([hasVehicleEdit, hasAssetLocationEdit, hasEditMyPrivate]) => {
        this.hasVehicleEditPermission = hasVehicleEdit;
        this.hasEditMyPrivatePermission = hasEditMyPrivate;
        if (hasVehicleEdit || (hasEditMyPrivate && isUsersPrivateVehicle)) {
          this.formGroup.enable();
          this.formGroup.controls.ExpenseSetupID.disable();
        } else {
          this.formGroup.disable();
        }

        if (!hasAssetLocationEdit) {
          this.formGroup.controls.IsAssetLocation?.disable();
        }
      });
  }

  private removeSelectedRow(): void {
    const { agGrid } = this.splitterService;
    const { api } = agGrid;
    const selectedNodes = api.getSelectedNodes();
    this.toolbarActionsService.selectNextNode(agGrid.api, selectedNodes);
    const { data, rowIndex } = selectedNodes[0];
    api.applyTransaction({ remove: [data!] });
    const lastIndex = api.getLastDisplayedRowIndex();
    const nextIndex = lastIndex > rowIndex! ? rowIndex! : lastIndex;
    api.getDisplayedRowAtIndex(nextIndex)?.setSelected(true, true);
  }

  private setMainGridsFilterModel(): void {
    const vehicleGridApi = this.agGridEntityService.getGridApi();

    if (vehicleGridApi.getFilterModel()['ShowRecord']) {
      vehicleGridApi.setFilterModel(QuickFilterModel.Active);
    }
  }

  private subscribeToVehicleDeletedTrigger(): void {
    this.eventBusService
      .on(EventKey.SidesheetEntityDeleted)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onEntityUpdate();
      });
  }

  private openRegistrationNumberChangedDialog(): DvDialogComponent<
    undefined,
    undefined
  > {
    return this.dialog.open({
      type: 'warning',
      title: 'General.Warning',
      confirmText: 'General.Yes',
      cancelText: 'General.No',
      contentText: 'Vehicle.RegistrationNumberChange.Warning',
    });
  }

  private addNewVehicle(): Observable<boolean> {
    const dirtyValues = DvFormUtils.getDirtyValues(this.formGroup);
    const data = this.formService.prepareDataForSave(dirtyValues);
    return this.api.upsert(Entity.Vehicle, 0, data).pipe(
      switchMap(response => {
        if (
          response &&
          typeof response === 'object' &&
          'CustomCheck' in response
        ) {
          return this.openOldTripsExistDialog();
        } else {
          this.formGroup.reset();
          this.formGroup.markAsPristine();
          if (response) {
            this.agGridEntityService.addNewlyCreatedItemToGrid(
              Entity.Vehicle,
              response
            );
          }
          return of(null);
        }
      }),
      concatMap(event => {
        if (event?.source === 'cancel') {
          return this.api.upsert(Entity.Vehicle, 0, {
            ...data,
            // skip specific vehicle check for tracker trips (required by backend)
            IsCustomCheckConfirmed: false,
          });
        } else if (event?.source === 'confirm') {
          return this.api.upsert(Entity.Vehicle, 0, {
            ...data,
            // execute custom vehicle tracker trips assignment (required by backend)
            IsCustomCheckConfirmed: true,
          });
        } else {
          return of(true);
        }
      }),
      tap(resp => {
        if (typeof resp === 'number') {
          this.formGroup.markAsPristine();
          this.agGridEntityService.addNewlyCreatedItemToGrid(
            Entity.Vehicle,
            resp
          );
        }
      }),
      map(() => true)
    );
  }

  private updateVehicle(
    dirtyValues: Partial<VehicleInformationTabForm>
  ): Observable<boolean> {
    const data = {
      ...this.formService.prepareDataForSave(dirtyValues),
      AllowShowInMap: this.formGroup.controls.AllowShowInMap?.value,
    };
    const entityId = this.entityId;
    const isVehicleCopyNeeded =
      this.hasVehicleTypeChanged && this.formGroup.controls.HasTrip?.value;
    if (isVehicleCopyNeeded) {
      return this.copyVehicle(Number(entityId));
    } else {
      return this.upsertExistingVehicle(entityId, data);
    }
  }

  private subscribeIsRouteNetworkMaintainer(): void {
    this.formGroup.controls.IsRouteNetworkMaintainer?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        const {
          IsPublic,
          RouteNetworkMaintainerTypeID,
          RouteNetworkMaintainer_HideIfNoExternalPower,
          RouteNetworkMaintainer_ParkedVisibilityDurationMinutes,
        } = this.formGroup.controls;

        const controlsArray: (FormControl<unknown> | undefined)[] = [
          IsPublic,
          RouteNetworkMaintainerTypeID,
          RouteNetworkMaintainer_HideIfNoExternalPower,
          RouteNetworkMaintainer_ParkedVisibilityDurationMinutes,
        ];

        controlsArray.forEach(c => {
          if (c) {
            DynamicFormUtils.enableDisableControlWithoutEvent(
              c,
              !!value && !!this.hasVehicleEditPermission
            );
          }
        });
      });
  }
}
